import apiClient from './apiClient';

const responseError = (body, error) => {
  const message =
    body?.error?.message ||
    body?.message ||
    error.message;
  const result = message ? new Error(message) : error;
  result.code = body?.error?.code || body?.code;
  result.details = body?.error?.details || body?.details;
  return result;
};
const handleError = (error) => responseError(error.response?.data, error);

// Trigger a browser download from a blob response.
const downloadBlob = (data, filename, type) => {
  const url = window.URL.createObjectURL(new Blob([data], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// If a blob response is actually a JSON error, decode and throw it.
const blobError = async (error) => {
  const blob = error.response && error.response.data;
  if (blob && typeof blob.text === 'function') {
    try {
      const parsed = JSON.parse(await blob.text());
      const msg = parsed?.error?.message || parsed?.message;
      if (msg) return responseError(parsed, error);
    } catch (e) { /* not JSON */ }
  }
  return handleError(error);
};

const salaryService = {
  previewExport: async (year, month, active, danlon, employeeIds) => {
    try {
      return (await apiClient.post('/admin/salary/export-plan', { employeeIds }, { params: { year, month, active, danlon } })).data.data;
    } catch (error) { throw handleError(error); }
  },
  transferToDanlon: async (year, month, payload) => {
    try {
      return (await apiClient.post('/admin/salary/danlon-transfer', payload, { params: { year, month } })).data.data;
    } catch (error) { throw handleError(error); }
  },
  getDanlonTransfers: async (year, month) => {
    try {
      return (await apiClient.get('/admin/salary/danlon-transfers', { params: { year, month } })).data.data;
    } catch (error) { throw handleError(error); }
  },
  resolveDanlonTransfer: async (id, outcome, reason) => {
    try {
      return (await apiClient.post(`/admin/salary/danlon-transfers/${encodeURIComponent(id)}/resolve`, { outcome, reason })).data.data;
    } catch (error) { throw handleError(error); }
  },
  updateTaxCard: async (employeeId, change) => {
    try {
      const response = await apiClient.put(`/admin/salary/employees/${employeeId}/tax-card`, change);
      return response.data.data;
    } catch (error) { throw handleError(error); }
  },
  confirmPayout: async (payment) => {
    try {
      const response = await apiClient.post('/admin/salary/payouts', payment);
      return response.data.data;
    } catch (error) { throw handleError(error); }
  },
  downloadPayoutReport: async (payoutId, format) => {
    try {
      const response = await apiClient.get(`/admin/salary/payouts/${payoutId}/report`, {
        params: { format }, responseType: 'blob',
      });
      const match = /filename="?([^";]+)"?/.exec(response.headers['content-disposition'] || '');
      downloadBlob(response.data, match ? match[1] : `tempus-payout-${payoutId}.${format}`,
        format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    } catch (error) { throw await blobError(error); }
  },
  // Month cohort: employment in the month, outstanding inputs and saved payouts.
  // Legacy filter arguments remain API-compatible; they do not determine eligibility.
  getOverview: async (year, month, active, danlon, payroll) => {
    try {
      const response = await apiClient.get('/admin/salary/overview', { params: { year, month, active, danlon, payroll } });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // "Afslut Payroll": take one employee off the payroll list, or put them back.
  // The Employees page and the Afslut button on a payroll row both call this, so
  // the two screens can never disagree about who is on the list.
  setPayrollStatus: async (employeeId, ended) => {
    try {
      const response = await apiClient.put(`/admin/salary/employees/${employeeId}/payroll-status`, { ended });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Save inline edits, or checkbox-only {employeeId, exportSelected} entries.
  // Selection is persisted per month without changing payroll amounts.
  saveEntries: async (year, month, entries) => {
    try {
      const response = await apiClient.put('/admin/salary/entries', { entries }, { params: { year, month } });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Overfør: carry an employee's payable hours from (year,month) into the next month (REQ #23).
  transfer: async (year, month, employeeId) => {
    try {
      const response = await apiClient.post('/admin/salary/transfer', { year, month, employeeId });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Contested days awaiting an admin decision (REQ #13).
  getDisputes: async (includeSettled = false) => {
    try {
      const response = await apiClient.get('/admin/salary/disputes', { params: { includeSettled } });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Settle a contested day: set the final hours that feed salary/invoice (REQ #13).
  settleDispute: async (timeEntryId, hours) => {
    try {
      const response = await apiClient.post(`/admin/salary/disputes/${timeEntryId}/settle`, { hours });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  // Download one atomic payroll snapshot as two files: checked Tax Card rows
  // in Danløn CSV, and selected rows without a tax card in a review workbook.
  generateCsv: async (year, month, active, danlon, employeeIds = [], confirmation = {}) => {
    try {
      // Body must be an object, not null: apiClient sends Content-Type
      // application/json, so a null body serializes to the string "null",
      // which express.json()'s strict mode rejects ("null" is not valid JSON).
      const response = await apiClient.post('/admin/salary/generate-csv', { employeeIds, ...confirmation, responseFormat: 'files' }, {
        params: { year, month, active, danlon },
      });
      const result = response.data.data;
      // Decode both before starting either download, so an incomplete response
      // never silently downloads only the CSV and loses the no-card report.
      if (!Array.isArray(result?.files) || result.files.length !== 2) throw new Error('Payroll files could not be downloaded. Generate them again.');
      const files = result.files.map((file) => ({ ...file,
        bytes: Uint8Array.from(window.atob(file.base64), (character) => character.charCodeAt(0)),
      }));
      files.forEach((file) => downloadBlob(file.bytes, file.filename, file.contentType));
      return { skipped: result.skipped || 0, csvEmployees: result.csvEmployees, noTaxCardEmployees: result.noTaxCardEmployees };
    } catch (error) {
      throw await blobError(error);
    }
  },

  // Download the salary Excel (empty or filled) for the month.
  downloadExcel: async (year, month, active, danlon) => {
    try {
      const response = await apiClient.get('/admin/salary/excel', {
        params: { year, month, active, danlon }, responseType: 'blob',
      });
      const cd = response.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      downloadBlob(
        response.data,
        m ? m[1] : `tempus-salary-${year}-${month}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      );
    } catch (error) {
      throw await blobError(error);
    }
  },

  // Import a filled salary Excel back into the month.
  importExcel: async (year, month, file) => {
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await apiClient.post('/admin/salary/import-excel', form, {
        params: { year, month }, headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  lockMonth: async (year, month) => {
    try {
      const response = await apiClient.post('/admin/salary/lock', { year, month });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  unlockMonth: async (year, month) => {
    try {
      const response = await apiClient.post('/admin/salary/unlock', { year, month });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },
};

export default salaryService;
